'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useGetSkillCompatibility } from '@/features/jobs/api/jobs.queries';
import { CheckSkillCompatibilityResponseDto } from '@/shared/job/job.public.dto';

export type JobCompatibilityState = {
  result: CheckSkillCompatibilityResponseDto | null;

  isChecking: boolean;
  isExpanded: boolean;
};

export type ChangeJobCompatibility = (
  patch: Partial<JobCompatibilityState>
) => void;

const INITIAL_STATE: JobCompatibilityState = {
  result: null,

  isChecking: false,
  isExpanded: false,
};

export function hasCompatibilityData(
  result?: CheckSkillCompatibilityResponseDto | null
): result is CheckSkillCompatibilityResponseDto {
  return (
    result != null &&
    (result.score !== undefined ||
      !!result.matchedSkills?.length ||
      !!result.missingSkills?.length)
  );
}

export function useJobCompatibility(jobId?: number) {
  const [state, setState] = useState<JobCompatibilityState>(INITIAL_STATE);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const { data: saved } = useGetSkillCompatibility(jobId ?? 0);

  const change = useCallback<ChangeJobCompatibility>((patch) => {
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  useEffect(() => {
    if (!hasCompatibilityData(saved)) return;
    setState((prev) => ({ ...prev, result: saved }));
  }, [saved]);

  const view = useCallback(() => {
    change({ isExpanded: true });
    requestAnimationFrame(() => {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, [change]);

  return { state, change, view, sectionRef };
}
